import { useState } from "react";
import type { RefObject } from "react";
import {
  Button,
  Card,
  ErrorMessage,
  FileUpload,
  Flex,
  Text,
  type FileObjectType,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { SPACING } from "~/styles/variables";
import * as styles from "./styles_vanilla.css";

export type CompletedDdqFile = {
  name: string;
  metaLabel: string;
};

export type CompletedDdqFileCardProps = {
  title: string;
  description: string;
  file: CompletedDdqFile | null;
  isUploading: boolean;
  uploadError: string | null;
  onFilesSelected: (accepted: FileObjectType[]) => void;
};

// Shared between the maker's "Upload the completed DDQ" card and the checker's "Review completed
// DDQ" card — per product decision, both show the same Download + Replace treatment once a file
// exists, rather than a maker-only delete affordance. The actual upload (S3 + uploadOriginalFormatFile)
// is orchestrated by the parent — this component only picks a file and reports it up.
export const CompletedDdqFileCard = ({
  title,
  description,
  file,
  isUploading,
  uploadError,
  onFilesSelected,
}: CompletedDdqFileCardProps) => {
  const [replaceInputRef, setReplaceInputRef] = useState<RefObject<HTMLInputElement> | null>(
    null,
  );

  return (
    <Card title={title} isFullWidth data-testid="original-format-file-card">
      {!file ? (
        <Flex flexDirection="column" gap={SPACING.SMALL}>
          <Text variant="regular-md" color={Colors.Neutral600}>
            {description}
          </Text>
          <FileUpload
            isFullWidth
            canAllowMultiple={false}
            showFileList={false}
            isLoading={isUploading}
            onNewFileUploaded={onFilesSelected}
            guidanceMessage="Make sure to include all attachments as a .zip file."
            alwaysShowGuidance
            data-testid="original-format-upload-dropzone"
          />
          {uploadError && (
            <ErrorMessage data-testid="original-format-upload-error">
              {uploadError}
            </ErrorMessage>
          )}
        </Flex>
      ) : (
        <Flex flexDirection="column" gap={SPACING.MEDIUM}>
          <Flex justifyContent="space-between" alignItems="center">
            <Text variant="strong-md">Completed DDQ</Text>
            <Button variant="neutral" disabled data-testid="original-format-download-completed">
              Download
            </Button>
          </Flex>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            className={styles.fileRow}
            data-testid="original-format-completed-file-row"
          >
            <Flex alignItems="center" gap={SPACING.SMALL}>
              <Icon type="file-alt" color={Colors.Neutral600} size="large" />
              <Flex flexDirection="column">
                <Text variant="regular-md">{file.name}</Text>
                <Text variant="regular-sm" color={Colors.Neutral600}>
                  {file.metaLabel}
                </Text>
              </Flex>
            </Flex>
            <Button
              variant="link"
              displayAsLink
              isInline
              disabled={isUploading}
              onClick={() => replaceInputRef?.current?.click()}
              data-testid="original-format-replace-file-trigger"
            >
              Replace file
            </Button>
            <FileUpload
              style={{ display: "none" }}
              canAllowMultiple={false}
              showFileList={false}
              isLoading={isUploading}
              onNewFileUploaded={onFilesSelected}
              innerRef={setReplaceInputRef}
              data-testid="original-format-replace-file-input"
            />
          </Flex>
          {uploadError && (
            <ErrorMessage data-testid="original-format-upload-error">
              {uploadError}
            </ErrorMessage>
          )}
        </Flex>
      )}
    </Card>
  );
};
